<template>
  <v-card class="commentCard d-flex flex-column" :class="{ 'user-comment': canEdit, 'edited-comment': isUpdated }" elevation="0" :key="`assembly-comment-${index}`">
    <div class="d-flex justify-space-between align-center">
      <UserAvatar :user="comment.createdBy" :hideRoles="true"></UserAvatar>
      <div class="d-flex align-center gap-2">
        <div class="d-flex flex-column align-end">
          <label style="color: var(--v-normal-grey-base); font-size: 12px;">
            <v-icon small color="normal-grey">mdi-clock-outline</v-icon> {{ timeElapsed }}
          </label>
          <label v-if="isUpdated" style="color: var(--v-normal-grey-base); font-size: 10px;" class="mt-1 edit-indicator">
            <v-icon x-small color="normal-grey">mdi-pencil-outline</v-icon> edited {{ timeElapsedUpdated }}
          </label>
        </div>
        <v-menu attach v-if="canEdit" bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              class="comment-menu-btn">
              <v-icon small color="normal-grey">mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="startEdit">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-pencil</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="confirmDelete" class="delete-item">
              <v-list-item-icon class="mr-2">
                <v-icon small color="error">mdi-delete</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="error--text">Delete</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
    
    <!-- Read-only view -->
    <v-tiptap
      v-if="!isEditing"
      :value="comment.content"
      :toolbar="[]"
      attach
      :mention="true"
      class="tiptap-section-card" />
    
    <!-- Edit mode -->
    <div v-else class="edit-mode">
      <v-tiptap
        v-model="editedContent"
        :toolbar="['bold', 'italic', 'underline', '|', 'bulletList', 'orderedList']"
        attach
        :mention="true"
        class="tiptap-section-card editable"
        placeholder="Edit your comment..." />
      <div class="d-flex justify-end mt-2 gap-2">
        <v-btn
          small
          outlined
          @click="cancelEdit"
          :disabled="updateLoading">
          Cancel
        </v-btn>
        <v-btn
          small
          color="primary"
          @click="saveEdit"
          :loading="updateLoading"
          :disabled="!editedContent || !editedContent.trim()">
          Save
        </v-btn>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400" style="z-index: 1998 !important;">
      <v-card>
        <v-card-title class="headline">
          <v-icon color="error" class="mr-2">mdi-delete</v-icon>
          Delete Comment
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this comment? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false" :disabled="deleteLoading">
            Cancel
          </v-btn>
          <v-btn 
            color="error" 
            @click="deleteComment" 
            :loading="deleteLoading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  import UserAvatar from '@Share/components/UserAvatar.vue';
  import { $timeElapsed } from '@/utils';
  import { mapGetters } from 'vuex';
  import { log, warn, error } from '@/utils/debugLogger';
  
  export default {
    components: {
      UserAvatar,
    },
    props: {
      comment: {
        type: Object,
        default: () => null,
      },
      index: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        isEditing: false,
        editedContent: '',
        showDeleteDialog: false,
        deleteLoading: false,
      };
    },
    computed: {
      ...mapGetters('USER', {
        users: 'GET_ENTRIES',
      }),
      ...mapGetters('ACCOUNT', {
        currentUser: 'GET_USER',
      }),
      ...mapGetters('TASK_COMMENTS', {
        updateLoading: 'GET_UPDATE_LOADING',
      }),
      timeElapsed() {
        return $timeElapsed(this.comment.createDate);
      },
      timeElapsedUpdated() {
        if (!this.isUpdated) return '';
        return $timeElapsed(this.comment.updateDate);
      },
      isUpdated() {
        // Check if comment has been updated
        const createdDate = this.comment.createDate;
        
        const updatedDate = this.comment.updateDate;
        
        if (!updatedDate || !createdDate) return false;
        
        // Consider it updated if there's more than 1 minute difference
        const created = new Date(createdDate);
        const updated = new Date(updatedDate);
        const diffInMs = updated.getTime() - created.getTime();
        
        return diffInMs > 5000; // More than 5 seconds difference
      },
      canEdit() {
        // User can edit their own comment
        return this.currentUser && this.comment.createdBy && 
               this.currentUser.id === this.comment.createdBy.id;
      },
    },
    methods: {
      startEdit() {
        this.isEditing = true;
        this.editedContent = this.comment.content;
        // Focus the editor after Vue updates the DOM
        this.$nextTick(() => {
          const editor = this.$el.querySelector('.editable .ProseMirror');
          if (editor) {
            editor.focus();
          }
        });
      },
      cancelEdit() {
        this.isEditing = false;
        this.editedContent = '';
      },
      async saveEdit() {
        if (!this.editedContent || !this.editedContent.trim()) return;
        
        try {
          await this.$store.dispatch('TASK_COMMENTS/UPDATE', {
            id: this.comment.id,
            taskId: this.comment.taskId,
            content: this.editedContent,
          });

          log({
            ...this.comment,
            content: this.editedContent
          })
          
          // Emit event to parent component with updated comment
          this.$emit('comment-updated', {
            ...this.comment,
            content: this.editedContent
          });
          
          this.isEditing = false;
          this.editedContent = '';
          
        } catch (error) {
          error('Error updating comment:', error);
          // Error handling is already done in the store action
        }
      },
      confirmDelete() {
        this.showDeleteDialog = true;
      },
      async deleteComment() {
        this.deleteLoading = true;
        
        try {
          await this.$store.dispatch('TASK_COMMENTS/DELETE', this.comment.id);
          
          // Emit event to parent component
          this.$emit('comment-deleted', this.comment);
          
          this.showDeleteDialog = false;
          
        } catch (error) {
          error('Error deleting comment:', error);
          // Error handling is already done in the store action
        } finally {
          this.deleteLoading = false;
        }
      },
    },
    mounted() {},
    destroyed() {},
    watch: {},
  };
</script>

<style lang="scss">
  .commentCard {
    padding: 15px;
    border-radius: 7px;
    border: 1px solid var(--v-light-grey-base) !important;
    gap: 12px;
    box-shadow: 0px 4px 8px 0px var(--v-light-grey-base) !important;
    
    .gap-2 {
      gap: 8px;
    }
    
    .comment-menu-btn {
      opacity: 0;
      transition: opacity 0.2s ease;
    }
    
    &:hover .comment-menu-btn {
      opacity: 1;
    }
    
    .delete-item:hover {
      background-color: rgba(244, 67, 54, 0.1) !important;
    }
  }

  .v-menu__content:has(.v-tiptap-mentions) {
    z-index: 2000 !important;
  }
  
  .tiptap-section-card {
    width: 100%;
    max-height: 150px;
    overflow-y: auto;
    
    &.editable {
      max-height: 200px; // Slightly larger for editing
      border: 1px solid var(--v-primary-base);
      border-radius: 4px;
      padding: 8px;
    }
  }
  
  .tiptap-section-card .v-input__slot .v-card {
    border: none !important;
    border-radius: 0px;
    background-color: transparent;
    
    .ProseMirror {
      min-height: auto !important;
      padding: 0px;
    }
  }
  
  // Read-only comments
  .tiptap-section-card:not(.editable) .v-input__slot .v-card .ProseMirror {
    pointer-events: none; /* Disable all pointer events */
    user-select: none; /* Disable text selection */
  }
  
  // Editable comments
  .tiptap-section-card.editable .v-input__slot .v-card .ProseMirror {
    pointer-events: auto;
    user-select: auto;
    min-height: 60px !important;
    cursor: text;
  }
  
  .edit-mode {
    width: 100%;
    
    .gap-2 {
      gap: 8px;
    }

    .tiptap-section-card.editable header {
      background-color: var(--v-bg8-base) !important;
    }

    .v-tiptap.tiptap-section-card.editable {
      padding: 0;
    }

    .tiptap.ProseMirror {
      padding: 8px 12px !important;
    }
  }

  .mention {
    color: #08c;
    text-decoration: underline;
    cursor: pointer;
  }
  
  .v-tiptap-mentions .v-list-item:hover {
    background-color: var(--v-primary-base) !important;
  }
  
  .v-tiptap-mentions .v-list-item {
    background-color: var(--v-light-grey-darken1) !important;
  }

  .commentCard.user-comment {
    box-shadow: inset 0px 0 2px 0px var(--v-primary-base) !important;
    background: #03624c05;
  }
</style>
